import React, {useState} from 'react';
import {Link} from 'react-router';
import PropTypes from 'prop-types';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import {useFluxStore} from 'client/framework';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import constants from 'client/constants';
import {IconButton, Menu, copyToClipboard, addToast} from '@albert-io/atomic';

import {useContentDiscoveryStore} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.react';
import {getQuestionSetsByGuideLevelIdQuery} from 'resources/Question/QuestionSet.queries';
import FoldersModal from 'components/PracticeView/FoldersModal/FoldersModal.react';
import AssignModal, {SmartAssignTooltip} from 'components/AssignModal/AssignModal.react';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {
  ManageRestrictionsModal,
  ADMIN_MANAGING_RESTRICTIONS_FLAG
} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import './actions-dropdown.scss';
import track from 'react-tracking';

function ActionsDropdown({
  guideLevel,
  link,
  quickAssign = true,
  questionSetIds: initialQuestionSetIds,
  questionCount: initialQuestionCount,
  showManageRestrictions = false,
  guideLevelTree,
  tracking
}) {
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [questionSetIds, setQuestionSetIds] = useState(() => initialQuestionSetIds || []);
  const [questionCount, setQuestionCount] = useState(() => initialQuestionCount || 0);
  const [showSmartModal, setShowSmartModal] = useState(false);
  const [showRestrictionsModal, setShowRestrictionsModal] = useState(false);

  const isSchoolAdministrator = useFluxStore(sessionStore, (store) =>
    store.isSchoolAdministrator()
  );

  const toggleRestrictionsModal = () => {
    setShowRestrictionsModal((prev) => !prev);
  };

  const trackClick = (content, options = {}) => {
    tracking.trackEvent({
      event: 'click',
      content,
      component: 'assign dropdown',
      type: 'dropdown item',
      ...options
    });
  };

  const handleQuickAssign = async () => {
    const guideLevelId = guideLevel.getId();
    const {setLegacyItemId, setLegacyItemType} = useContentDiscoveryStore.getState().draft;
    trackClick('Assign all');
    setLegacyItemId(guideLevelId);
    setLegacyItemType('guide');
    history.pushState(null, `/assignment-builder/create-assignment/settings`);
  };

  const handleSmartAssign = () => {
    const countOfQuestions = guideLevel.getMeta().getCountOfQuestions();
    const minSizeQuestionSet = guideLevel.getMeta().getMinSizeQuestionSet();
    trackClick('Smart assign');

    if (countOfQuestions === 1) {
      handleQuickAssign();
    } else if (countOfQuestions === minSizeQuestionSet) {
      handleQuickAssign();
    } else {
      setShowSmartModal(true);
    }
  };

  /**
   * Fetches question sets and sets state
   */
  const fetchQuestionSets = async () => {
    if (questionSetIds.length) {
      return;
    }

    const questionSets = await getQuestionSetsByGuideLevelIdQuery(
      guideLevel.getId()
    ).getResourcePromise();
    const nextQuestionSetIds = [];
    let nextQuestionCount = 0;
    questionSets.forEach((set) => {
      nextQuestionSetIds.push(set.getId());
      nextQuestionCount += set.getQuestions().size;
    });
    setQuestionSetIds(nextQuestionSetIds);
    setQuestionCount(nextQuestionCount);
  };

  const toggleFoldersModal = () => {
    const nextShowFolderModal = !showFolderModal;
    setShowFolderModal(nextShowFolderModal);
    if (nextShowFolderModal) {
      fetchQuestionSets();
    }
  };

  const handleView = () => {
    history.pushState(null, link);
  };

  const handleCopy = () => {
    copyToClipboard(`${constants.ALBERT_FULL_PATH}${link}`, () => {
      addToast({
        color: 'positive',
        title: 'Link copied!',
        message: 'Share this link.'
      });
    });
  };

  return (
    <>
      <Menu closeOnItemClick position='bottom-end' wrapperClassName='actions-dropdown__wrapper'>
        <Menu.Trigger>
          {(triggerProps) => (
            <IconButton
              {...triggerProps}
              label='Actions'
              className='actions-dropdown__button'
              icon='ellipsis-v'
              onClick={(e) => {
                triggerProps.onClick(e);
                trackClick('Assign', {component: 'Toolbar', type: 'dropdown'});
              }}
            />
          )}
        </Menu.Trigger>
        <Menu.Content>
          {link && (
            <>
              <Menu.Item
                as={Link}
                to={link}
                className='no-underline'
                preventDefaultClickHandler={false}
                onClick={() => trackClick('View')}
              >
                View
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          {quickAssign && (
            <Menu.Item
              onClick={() => {
                handleQuickAssign();
                trackClick('Assign all');
              }}
            >
              {`Assign${questionCount !== 1 ? ' All' : ''}`}
            </Menu.Item>
          )}
          <Menu.Item onClick={handleSmartAssign}>
            <SmartAssignTooltip className='grow' leftContent='Smart Assign' />
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              toggleFoldersModal();
              trackClick('Save to Folders');
            }}
          >
            Save to Folders
          </Menu.Item>
          {link && (
            <Menu.Item
              onClick={() => {
                handleCopy();
                trackClick('Copy Link');
              }}
            >
              Copy Link
            </Menu.Item>
          )}
          {showManageRestrictions && isSchoolAdministrator && (
            <FeatureFlag name={ADMIN_MANAGING_RESTRICTIONS_FLAG}>
              <Menu.Divider />
              <Menu.Item
                onClick={() => {
                  toggleRestrictionsModal();
                  trackClick('Manage Restrictions');
                }}
                rightIconProps={{icon: 'lock', iconStyle: 'regular'}}
              >
                Manage Restrictions
              </Menu.Item>
            </FeatureFlag>
          )}
        </Menu.Content>
      </Menu>
      {showManageRestrictions && showRestrictionsModal && (
        <ManageRestrictionsModal
          guideLevelName={guideLevel.getName()}
          guideLevelId={guideLevel.getId()}
          guideLevelTree={guideLevelTree}
          onClose={() => setShowRestrictionsModal(false)}
        />
      )}
      {showFolderModal && (
        <FoldersModal
          selectedQuestionSetIds={questionSetIds}
          closeModalFunc={toggleFoldersModal}
          selectedQuestionCount={questionCount}
        />
      )}
      {showSmartModal && (
        <AssignModal
          resource={guideLevel}
          type='guide'
          onClose={() => {
            setShowSmartModal(false);
          }}
        />
      )}
    </>
  );
}

ActionsDropdown.propTypes = {
  guideLevel: PropTypes.instanceOf(GuideLevelModelV2),
  link: PropTypes.string,
  quickAssign: PropTypes.bool,
  questionSetIds: PropTypes.array,
  questionCount: PropTypes.number,
  // Only guide-level surfaces expose the restrict action — not individual question rows.
  showManageRestrictions: PropTypes.bool,
  guideLevelTree: PropTypes.object,
  tracking: PropTypes.object
};

export default track()(ActionsDropdown);
